import React, { useEffect, useState, useCallback } from "react";
import { Sun, Loader2, Trash2, MessageCircle, Copy, Download, UserMinus, UserCheck } from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

export default function SubscribersPanel() {
  const [items, setItems] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [darshan, setDarshan] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([api.get("/admin/subscribers"), api.get("/darshan/today")]);
      setItems(s.data.items || []);
      setActiveCount(s.data.active || 0);
      setDarshan(d.data || null);
    } catch { toast.error("Could not load subscribers"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const message = darshan
    ? `🪔 Daily Darshan — TarapithTantrik.com\n\n“${darshan.text_en}”\n\nMaa Tara's blessings. — Shivam Chatterjee`
    : "";

  const copy = async (value, label) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success(`${label} copied`);
    } catch { toast.error("Could not copy"); }
  };

  const toggle = async (sub) => {
    try {
      await api.patch(`/admin/subscribers/${sub.id}`, { active: !sub.active });
      load();
    } catch { toast.error("Update failed"); }
  };

  const remove = async (id) => {
    try {
      await api.delete(`/admin/subscribers/${id}`);
      setItems((p) => p.filter((x) => x.id !== id));
      toast.success("Removed");
    } catch { toast.error("Delete failed"); }
  };

  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/subscribers/export", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "text/csv" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `darshan-subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("CSV downloaded");
    } catch { toast.error("Export failed"); }
  };

  const allNumbers = items.filter((i) => i.active).map((i) => i.phone).join(", ");

  return (
    <div data-testid="subscribers-panel">
      <div className="grid lg:grid-cols-[1fr_320px] gap-6 mb-8">
        <div className="glass-liquid rounded-2xl p-6" data-testid="subscribers-message-box">
          <h3 className="font-serif text-sm uppercase tracking-[0.18em] text-tara-goldDeep mb-3 flex items-center gap-2">
            <Sun className="h-3.5 w-3.5" /> Today's darshan message
          </h3>
          {darshan ? (
            <>
              <p className="font-display text-lg text-tara-ink italic leading-relaxed mb-4">“{darshan.text_en}”</p>
              <div className="flex flex-wrap gap-2.5">
                <button onClick={() => copy(message, "Message")} className="btn-sacred flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold" data-testid="copy-message-btn">
                  <Copy className="h-3.5 w-3.5" /> Copy message
                </button>
                <button onClick={() => copy(allNumbers, "Numbers")} className="btn-ghost-gold flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold" data-testid="copy-numbers-btn">
                  <Copy className="h-3.5 w-3.5" /> Copy all numbers
                </button>
                <button onClick={exportCsv} className="btn-ghost-gold flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-semibold" data-testid="subscribers-export-btn">
                  <Download className="h-3.5 w-3.5" /> CSV
                </button>
              </div>
              <p className="text-[11px] text-tara-muted mt-4 leading-relaxed">
                Paste the message into a WhatsApp broadcast list (Settings → Broadcast lists) to reach everyone at once,
                or tap the WhatsApp icon beside a devotee to send it individually.
              </p>
            </>
          ) : (
            <p className="text-sm text-tara-muted">Publish a reflection in the Daily Darshan tab first.</p>
          )}
        </div>

        <div className="glass rounded-2xl p-6 flex flex-col justify-center">
          <p className="font-serif text-4xl font-bold text-tara-ink leading-none" data-testid="subscribers-active-count">{activeCount}</p>
          <p className="text-xs text-tara-muted uppercase tracking-[0.16em] mt-2">Active subscribers</p>
          <p className="text-xs text-tara-muted mt-4">{items.length} total, {items.length - activeCount} paused</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 text-tara-gold animate-spin" /></div>
      ) : items.length === 0 ? (
        <div className="glass rounded-2xl p-12 text-center text-tara-muted" data-testid="subscribers-empty">
          <Sun className="h-10 w-10 mx-auto mb-3 text-tara-muted/70" />
          No subscribers yet. Devotees can join from the Daily Darshan card on the homepage.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2" data-testid="subscribers-list">
          {items.map((s) => (
            <div key={s.id} className={`glass rounded-2xl p-4 flex items-start justify-between gap-3 ${s.active ? "" : "opacity-60"}`} data-testid={`subscriber-${s.id}`}>
              <div className="min-w-0">
                <p className="font-display text-lg text-tara-ink leading-tight">{s.name}</p>
                <p className="text-xs text-tara-muted mt-1">
                  {s.phone}
                  <span className="ml-2 rounded-full bg-white/70 border border-tara-gold/20 px-2 py-0.5 uppercase">{s.language}</span>
                  {!s.active && <span className="ml-2 text-tara-muted">paused</span>}
                </p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <a
                  href={`https://wa.me/${s.phone.replace(/\D/g, "")}?text=${encodeURIComponent(message)}`}
                  target="_blank" rel="noopener noreferrer"
                  className="rounded-full border border-green-500/40 p-2 text-green-600 hover:bg-green-500/10 transition"
                  data-testid={`subscriber-whatsapp-${s.id}`}
                  aria-label="Send today's darshan"
                >
                  <MessageCircle className="h-4 w-4" />
                </a>
                <button onClick={() => toggle(s)} className="rounded-full border border-tara-gold/30 p-2 text-tara-goldDeep hover:bg-tara-gold/10 transition" data-testid={`subscriber-toggle-${s.id}`} aria-label="Toggle active">
                  {s.active ? <UserMinus className="h-4 w-4" /> : <UserCheck className="h-4 w-4" />}
                </button>
                <button onClick={() => remove(s.id)} className="rounded-full border border-red-500/40 p-2 text-red-600 hover:bg-red-500/10 transition" data-testid={`subscriber-delete-${s.id}`} aria-label="Delete">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
